import AddBoxSharpIcon from '@mui/icons-material/AddBoxSharp';
import DisabledByDefaultSharpIcon from '@mui/icons-material/DisabledByDefaultSharp';
import { ButtonBase, Stack, Typography } from '@mui/material';
import { QuestionnaireLanguages, i18nQuestion } from 'features/lead/lead-management/lead-questionnaire-details/lead-questionnaire-details.types';
import PaiTextField from 'features/ui/base-components/pai-text-field';
import React from 'react';
import { makeStyles } from 'tss-react/mui';
import { BasicActionDialog } from '../../../ui/base-dialogs';

interface Props {
  open: boolean;
  questions: i18nQuestion[];

  handleClose(): void;

  handleProceed(): void;

  addNewOpenQuestion(): void;

  removeOpenQuestion(questionIndex: number): () => void;

  handleOpenSelfAssessmentQuestionChange(questionIndex: number, lang: QuestionnaireLanguages): (event: React.ChangeEvent<HTMLInputElement>) => void;

  handleOpenExternalAssessmentQuestionChange(
    questionIndex: number,
    lang: QuestionnaireLanguages
  ): (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const AddNewOpenQuestionsDialog: React.FC<Props> = ({
  open,
  questions,
  handleProceed,
  handleClose,
  addNewOpenQuestion,
  removeOpenQuestion,
  handleOpenExternalAssessmentQuestionChange,
  handleOpenSelfAssessmentQuestionChange,
}) => {
  const { classes } = useStyles();

  const isEmpty = () => {
    if (
      questions.some((question) => question.selfAssessment.de.trim() === '') ||
      questions.some((question) => question.selfAssessment.en.trim() === '') ||
      questions.some((question) => question.externalAssessment.de.trim() === '') ||
      questions.some((question) => question.externalAssessment.en.trim() === '')
    ) {
      return true;
    }

    return false;
  };

  return (
    <BasicActionDialog
      dialogTitle={'Persönliche Eindrücke hinzufügen'}
      maxWidth={'md'}
      cancelButtonText={'Abbrechen'}
      proceedButtonText={'Hinzufügen'}
      open={open}
      handleClose={handleClose}
      handleProceed={handleProceed}
      proceedButtonDisabled={isEmpty()}
    >
      <Stack gap={2}>
        {questions.map((question, index) => (
          <Stack key={index} className={classes.container} gap={2}>
            <Stack direction={'row'} alignContent={'center'} gap={2}>
              <PaiTextField
                value={question.selfAssessment.de}
                placeholder={'Fragetext zur Selbstbewertung...'}
                label={'Selbstbewertung (deutsch)'}
                onChange={handleOpenSelfAssessmentQuestionChange(index, QuestionnaireLanguages.DE)}
              />
              <PaiTextField
                value={question.selfAssessment.en}
                placeholder={'Fragetext zur Selbstbewertung...'}
                label={'Selbstbewertung (englisch)'}
                onChange={handleOpenSelfAssessmentQuestionChange(index, QuestionnaireLanguages.EN)}
              />
            </Stack>
            <Stack key={index} direction={'row'} alignContent={'center'} gap={2}>
              <PaiTextField
                value={question.externalAssessment.de}
                placeholder={'Fragetext zur Fremdbewertung...'}
                label={'Fremdbewertung (deutsch)'}
                onChange={handleOpenExternalAssessmentQuestionChange(index, QuestionnaireLanguages.DE)}
              />
              <PaiTextField
                value={question.externalAssessment.en}
                placeholder={'Fragetext zur Fremdbewertung...'}
                label={'Fremdbewertung (englisch)'}
                onChange={handleOpenExternalAssessmentQuestionChange(index, QuestionnaireLanguages.EN)}
              />
            </Stack>
            {questions.length > 1 ? (
              <Stack direction={'row'} justifyContent={'flex-end'}>
                <ButtonBase className={classes.deleteButton} onClick={removeOpenQuestion(index)}>
                  <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} gap={1}>
                    <DisabledByDefaultSharpIcon className={classes.icon} />
                    <Typography className={classes.text}>Frage entfernen</Typography>
                  </Stack>
                </ButtonBase>
              </Stack>
            ) : null}
          </Stack>
        ))}
        <ButtonBase className={classes.addButton} onClick={addNewOpenQuestion}>
          <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} gap={1}>
            <AddBoxSharpIcon className={classes.icon} />
            <Typography className={classes.text}>weitere Frage</Typography>
          </Stack>
        </ButtonBase>
      </Stack>
    </BasicActionDialog>
  );
};

const useStyles = makeStyles()((theme) => ({
  container: {
    borderBottom: `1px solid ${theme.palette.greys.light}`,
    paddingBottom: '16px',
  },
  deleteButton: {
    color: theme.palette.error.main,
    '&:hover': {
      color: theme.palette.secondary.main,
    },
  },
  addButton: {
    marginRight: 'auto',
    color: theme.palette.primary.main,
    '&:hover': {
      color: theme.palette.secondary.main,
    },
  },
  icon: {
    fontSize: theme.typography.pxToRem(20),
  },
  text: {
    fontSize: theme.typography.pxToRem(13),
    textDecoration: 'underline',
    textUnderlineOffset: '4px',
  },
}));

export default AddNewOpenQuestionsDialog;
